import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RegulatoryCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date(2025, 8, 21)); // September 2025
  const [selectedView, setSelectedView] = useState('month');

  const regulatoryEvents = [
    {
      id: 1,
      date: '2025-09-22',
      title: 'MiFID II Transaction Reporting',
      type: 'deadline',
      category: 'EUA',
      priority: 'critical',
      description: 'Submit transaction reports for EUA trades to relevant authorities',
      status: 'pending',
      reminderSet: true
    },
    {
      id: 2,
      date: '2025-09-23',
      title: 'CFTC Position Reporting',
      type: 'deadline',
      category: 'Oil',
      priority: 'high',
      description: 'File large trader position reports for oil futures contracts',
      status: 'in-progress',
      reminderSet: true
    },
    {
      id: 3,
      date: '2025-09-25',
      title: 'EU ETS Registry Maintenance',
      type: 'event',
      category: 'EUA',
      priority: 'medium',
      description: 'Scheduled maintenance window for EU ETS registry system',
      status: 'scheduled',
      reminderSet: false
    },
    {
      id: 4,
      date: '2025-09-30',
      title: 'Monthly Risk Report',
      type: 'deadline',
      category: 'General',
      priority: 'high',
      description: 'Submit monthly risk management report to board',
      status: 'pending',
      reminderSet: true
    },
    {
      id: 5,
      date: '2025-10-01',
      title: 'Q4 Compliance Review',
      type: 'meeting',
      category: 'General',
      priority: 'medium',
      description: 'Quarterly compliance review meeting with regulators',
      status: 'scheduled',
      reminderSet: true
    },
    {
      id: 6,
      date: '2025-10-05',
      title: 'EMIR Trade Reporting',
      type: 'deadline',
      category: 'Oil',
      priority: 'critical',
      description: 'Submit derivative trade reports under EMIR regulation',
      status: 'pending',
      reminderSet: false
    },
    {
      id: 7,
      date: '2025-10-15',
      title: 'Carbon Credit Verification',
      type: 'deadline',
      category: 'EUA',
      priority: 'high',
      description: 'Annual carbon credit verification and certification',
      status: 'pending',
      reminderSet: true
    }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'border-l-error bg-error/5';
      case 'high': return 'border-l-warning bg-warning/5';
      case 'medium': return 'border-l-accent bg-accent/5';
      case 'low': return 'border-l-muted-foreground bg-muted/5';
      default: return 'border-l-muted-foreground bg-muted/5';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'deadline': return 'Clock';
      case 'event': return 'Calendar';
      case 'meeting': return 'Users';
      default: return 'Calendar';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success/10 border-success/20';
      case 'in-progress': return 'text-warning bg-warning/10 border-warning/20';
      case 'pending': return 'text-accent bg-accent/10 border-accent/20';
      case 'scheduled': return 'text-muted-foreground bg-muted border-border';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0) return `In ${diffDays} days`;
    if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
    
    return date?.toLocaleDateString();
  };

  const getUpcomingEvents = () => {
    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    return regulatoryEvents?.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate >= today && eventDate <= nextWeek;
      })?.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const upcomingEvents = getUpcomingEvents();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate?.setMonth(currentDate?.getMonth() + direction);
    setCurrentDate(newDate);
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-subtle">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={18} className="text-warning" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Regulatory Calendar</h2>
              <p className="text-sm text-muted-foreground">Track deadlines and compliance events</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-muted rounded-md p-1">
              {['week', 'month']?.map(view => (
                <button
                  key={view}
                  onClick={() => setSelectedView(view)}
                  className={`
                    px-3 py-1 rounded text-sm font-medium transition-colors duration-200
                    ${selectedView === view
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  {view?.charAt(0)?.toUpperCase() + view?.slice(1)}
                </button>
              ))}
            </div>
            <Button variant="outline" iconName="Plus" iconPosition="left" size="sm">
              Add Event
            </Button>
          </div>
        </div>

        {/* Calendar Navigation */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h3 className="text-xl font-semibold text-foreground">
              {monthNames?.[currentDate?.getMonth()]} {currentDate?.getFullYear()}
            </h3>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)} iconName="ChevronLeft" />
              <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)} iconName="ChevronRight" />
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setCurrentDate(new Date())}>
            Today
          </Button>
        </div>
      </div>
      <div className="p-6">
        {selectedView === 'month' && (
          <div className="space-y-4">
            <h4 className="font-medium text-foreground mb-4">Upcoming Events (Next 7 Days)</h4>
            {upcomingEvents?.length > 0 ? (
              <div className="space-y-3">
                {upcomingEvents?.map(event => (
                  <div key={event?.id} className={`border-l-4 p-4 rounded-r-lg ${getPriorityColor(event?.priority)}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <Icon name={getTypeIcon(event?.type)} size={18} className="text-muted-foreground mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h5 className="font-medium text-foreground">{event?.title}</h5>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(event?.status)}`}>
                              {event?.status?.replace('-', ' ')}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{event?.description}</p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Icon name="Calendar" size={12} />
                              <span>{new Date(event.date)?.toLocaleDateString()}</span>
                              <span className="font-medium">({formatDate(event?.date)})</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Icon name="Tag" size={12} />
                              <span>{event?.category}</span>
                            </div>
                            {event?.reminderSet && (
                              <div className="flex items-center gap-1 text-accent">
                                <Icon name="Bell" size={12} />
                                <span>Reminder set</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-4">
                        <Button variant="ghost" size="sm" iconName="Edit" />
                        <Button variant="ghost" size="sm" iconName="MoreHorizontal" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No upcoming events</h3>
                <p className="text-muted-foreground">No regulatory events scheduled for the next 7 days.</p>
              </div>
            )}

            {/* All Events Summary */}
            <div className="mt-8 pt-6 border-t border-border">
              <h4 className="font-medium text-foreground mb-4">All Scheduled Events</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {regulatoryEvents?.map(event => (
                  <div key={event?.id} className="border border-border rounded-lg p-4 hover:shadow-subtle transition-shadow duration-200">
                    <div className="flex items-start justify-between mb-2">
                      <Icon name={getTypeIcon(event?.type)} size={16} className="text-muted-foreground mt-1" />
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(event?.status)}`}>
                        {event?.status?.replace('-', ' ')}
                      </span>
                    </div>
                    <h5 className="font-medium text-foreground mb-1">{event?.title}</h5>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{event?.description}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{new Date(event.date)?.toLocaleDateString()}</span>
                      <span className="font-medium">{event?.category}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {selectedView === 'week' && (
          <div className="text-center py-12">
            <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">Week View</h3>
            <p className="text-muted-foreground">Week view functionality coming soon.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegulatoryCalendar;